/**
 * Affiliate Tracking Utility
 * Handles click tracking and cookie management for WillTheyConvert affiliate program
 */

// Configuration from environment variables
const AFFILIATE_HUB_URL = import.meta.env.VITE_AFFILIATE_HUB_API_URL || 'https://iqwiuhjtfnyrdxaasogn.supabase.co/functions/v1'
const COOKIE_NAME = import.meta.env.VITE_AFFILIATE_COOKIE_NAME || 'wtc_ref'
const COOKIE_DURATION_DAYS = parseInt(import.meta.env.VITE_AFFILIATE_COOKIE_DURATION_DAYS || '60')

interface TrackClickParams {
  affiliateCode: string
  referrerUrl?: string
  landingPage?: string
}

interface TrackConversionParams {
  userId: string
  orderId: string
  amount: number
  planName: string
  planInterval: 'monthly' | 'yearly' | 'lifetime'
  affiliateCode?: string // Optional - will use cookie if not provided
}

/**
 * Track affiliate link click
 * Call this when user arrives via ?ref=AFFILIATE_CODE
 */
export async function trackAffiliateClick(params: TrackClickParams): Promise<void> {
  try {
    // Store affiliate code in cookie for attribution
    setAffiliateCookie(params.affiliateCode)

    // Call our Supabase Edge Function for security
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
    const response = await fetch(`${supabaseUrl}/functions/v1/affiliate-track-click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`
      },
      body: JSON.stringify({
        affiliateCode: params.affiliateCode,
        referrerUrl: params.referrerUrl || document.referrer,
        landingPage: params.landingPage || window.location.href,
        userAgent: navigator.userAgent,
        deviceType: getDeviceType(),
        browser: getBrowser(),
        os: getOS()
      })
    })

    if (!response.ok) {
      // Failed to track affiliate click
    }
  } catch (error) {
    // Never fail user experience due to tracking errors
  }
}

/**
 * Track conversion (purchase/signup)
 * Call this when user completes payment
 */
export async function trackAffiliateConversion(params: TrackConversionParams): Promise<void> {
  try {
    // Get affiliate code from cookie if not provided
    const affiliateCode = params.affiliateCode || getAffiliateCookie()

    if (!affiliateCode) {
      return
    }

    // Use our Supabase Edge Function instead of calling Affiliate Hub directly for security
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
    const response = await fetch(`${supabaseUrl}/functions/v1/affiliate-track-conversion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`
      },
      body: JSON.stringify({
        userId: params.userId,
        orderId: params.orderId,
        amount: params.amount,
        planName: params.planName,
        planInterval: params.planInterval,
        affiliateCode: affiliateCode
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Conversion tracking failed: ${errorText}`)
    }

    const data = await response.json()

    if (data.success) {
      // Clear cookie after successful conversion
      clearAffiliateCookie()
    }
  } catch (error) {
    console.error('Error tracking affiliate conversion:', error)
    // Re-throw to handle in payment flow
    throw error
  }
}

/**
 * Set affiliate cookie with 60-day expiration
 */
function setAffiliateCookie(affiliateCode: string): void {
  const expiryDays = COOKIE_DURATION_DAYS
  const expiryDate = new Date()
  expiryDate.setDate(expiryDate.getDate() + expiryDays)

  // Use secure cookie settings
  const cookieOptions = [
    `${COOKIE_NAME}=${affiliateCode}`,
    `expires=${expiryDate.toUTCString()}`,
    'path=/',
    'SameSite=Lax'
  ]

  // Add Secure flag if on HTTPS
  if (window.location.protocol === 'https:') {
    cookieOptions.push('Secure')
  }

  document.cookie = cookieOptions.join('; ')
}

/**
 * Get affiliate cookie value
 */
export function getAffiliateCookie(): string | null {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === COOKIE_NAME) {
      return value
    }
  }
  return null
}

/**
 * Clear affiliate cookie
 */
function clearAffiliateCookie(): void {
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
}

/**
 * Check if user came from affiliate link
 */
export function hasAffiliate(): boolean {
  return getAffiliateCookie() !== null
}

/**
 * Get current affiliate code
 */
export function getCurrentAffiliateCode(): string | null {
  return getAffiliateCookie()
}

// Helper functions for device detection
function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const ua = navigator.userAgent.toLowerCase()
  if (/(tablet|ipad)/i.test(ua)) return 'tablet'
  if (/mobile/i.test(ua)) return 'mobile'
  return 'desktop'
}

function getBrowser(): string {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('firefox')) return 'Firefox'
  if (ua.includes('chrome') && !ua.includes('edg')) return 'Chrome'
  if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari'
  if (ua.includes('edg')) return 'Edge'
  if (ua.includes('opera') || ua.includes('opr')) return 'Opera'
  return 'Unknown'
}

function getOS(): string {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('win')) return 'Windows'
  if (ua.includes('mac')) return 'macOS'
  if (ua.includes('linux')) return 'Linux'
  if (ua.includes('android')) return 'Android'
  if (ua.includes('iphone') || ua.includes('ipad')) return 'iOS'
  return 'Unknown'
}